<template>
  <v-container class="pa-0">
    <v-row dense align="center">
      <!-- Unidad de negocio (selección múltiple + Todas) -->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="selectedBusiness"
          :items="businessItems"
          item-text="text"
          item-value="id"
          label="Unidad de negocio"
          color="cyanText"
          prepend-inner-icon="corporate_fare"
          outlined
          dense
          hide-details
          multiple
          small-chips
          :disabled="loading"
          @change="onBusinessChange"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" small label>{{ item.text }}</v-chip>
            <span v-else-if="index === 2" class="grey--text caption">
              (+{{ selectedBusiness.length - 2 }} más)
            </span>
          </template>
        </v-autocomplete>
      </v-col>

      <!-- Location (selección múltiple + Todos) -->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="selectedLocations"
          :items="locationItems"
          item-text="text"
          item-value="id"
          label="Location"
          color="cyanText"
          prepend-inner-icon="my_location"
          outlined
          dense
          hide-details
          multiple
          small-chips
          :disabled="loading"
          @change="onLocationsChange"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" small label>{{ item.text }}</v-chip>
            <span v-else-if="index === 2" class="grey--text caption">
              (+{{ selectedLocations.length - 2 }} más)
            </span>
          </template>
        </v-autocomplete>
      </v-col>

      <!-- Departamento -->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="department_id"
          :items="itemsDepartment"
          item-text="text"
          item-value="value"
          label="Departamento"
          color="cyanText"
          prepend-inner-icon="business"
          outlined
          dense
          hide-details
          :disabled="loading"
          @change="debouncedEmit"
        ></v-autocomplete>
      </v-col>

      <!-- Proyecto -->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="project_id"
          :items="itemsProjects"
          :item-text="projectText"
          item-value="id"
          label="Proyecto"
          color="cyanText"
          prepend-inner-icon="folder_open"
          outlined
          dense
          hide-details
          :disabled="loading"
          @change="debouncedEmit"
        ></v-autocomplete>
      </v-col>
    </v-row>

    <v-row dense align="center" class="mt-1">
      <!-- Estatus (selección múltiple) -->
      <v-col cols="12" sm="12" md="5">
        <v-autocomplete
          v-model="selectedStatuses"
          :items="statusItems"
          item-text="name"
          item-value="id"
          label="Estatus a mostrar"
          color="cyanText"
          prepend-inner-icon="tune"
          outlined
          dense
          hide-details
          multiple
          small-chips
          :disabled="loading"
          @change="debouncedEmit"
        >
          <template v-slot:prepend-item>
            <v-list-item ripple @mousedown.prevent @click="toggleAllStatuses">
              <v-list-item-action>
                <v-icon :color="selectedStatuses.length ? 'primary' : ''">
                  {{ allStatusesSelected ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline' }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ allStatusesSelected ? 'Quitar todos' : 'Seleccionar todos' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-1"></v-divider>
          </template>
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" small label>{{ item.name }}</v-chip>
            <span v-else-if="index === 2" class="grey--text caption">
              (+{{ selectedStatuses.length - 2 }} más)
            </span>
          </template>
        </v-autocomplete>
      </v-col>

      <!-- Rango de fechas -->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="rangeSelected"
          :items="rangeTime"
          label="Rango"
          color="cyanText"
          prepend-inner-icon="mdi-calendar"
          outlined
          dense
          hide-details
          :disabled="loading"
          @change="updateRange"
        ></v-autocomplete>
      </v-col>

      <v-col cols="6" sm="3" md="2" v-show="rangeSelected === '5'">
        <v-menu v-model="menuStart" :close-on-content-click="false" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="dateFormatDMY(dateStart)"
              label="Desde"
              prepend-inner-icon="mdi-calendar"
              readonly
              outlined
              dense
              hide-details
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker :max="dateEnd" v-model="dateStart" @input="(menuStart = false), debouncedEmit()"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="6" sm="3" md="2" v-show="rangeSelected === '5'">
        <v-menu v-model="menuEnd" :close-on-content-click="false" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="dateFormatDMY(dateEnd)"
              label="Hasta"
              prepend-inner-icon="mdi-calendar"
              readonly
              outlined
              dense
              hide-details
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker :min="dateStart" v-model="dateEnd" @input="(menuEnd = false), debouncedEmit()"></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" md="2" v-show="rangeSelected !== '5'">
        <v-btn block color="primary" outlined small :disabled="loading" @click="debouncedEmit">
          <v-icon left small>refresh</v-icon> Actualizar
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  name: "ReportFilters",
  props: {
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    selectedBusiness: [0], // 0 = Todas
    prevBusiness: [0],
    selectedLocations: [0], // 0 = Todos
    prevLocations: [0],
    department_id: 0,
    project_id: 0,
    selectedStatuses: [],
    itemsDepartment: [{ value: 0, text: "Todos" }],
    itemsProjects: [{ id: 0, name: "Todos" }],
    statusItems: [],
    excludedByDefault: [1, 11, 12], // Borrador, Cancelado, Rechazado
    rangeSelected: "4", // Este año
    rangeTime: [
      { text: "Hoy", value: "1" },
      { text: "Esta semana", value: "2" },
      { text: "Este mes", value: "3" },
      { text: "Este año", value: "4" },
      { text: "Personalizado", value: "5" },
    ],
    dateStart: moment().startOf("year").format("YYYY-MM-DD"),
    dateEnd: moment().endOf("year").format("YYYY-MM-DD"),
    menuStart: false,
    menuEnd: false,
  }),
  computed: {
    businessItems() {
      const units = this.$store.state.auth.bussines_units || [];
      return [
        { id: 0, text: "Todas" },
        ...units.map((u) => ({ id: u.id, text: u.description_long })),
      ];
    },
    locationItems() {
      const all = this.$store.state.auth.locations || [];
      const locations = this.selectedBusiness.includes(0)
        ? all
        : all.filter((l) => this.selectedBusiness.includes(l.bus_id));
      return [{ id: 0, text: "Todos" }, ...locations];
    },
    allStatusesSelected() {
      return (
        this.statusItems.length > 0 &&
        this.selectedStatuses.length === this.statusItems.length
      );
    },
  },
  async created() {
    await this.initialize();
  },
  methods: {
    projectText(project) {
      return project.code ? `${project.name} (${project.code})` : project.name;
    },
    async initialize() {
      // Unidad de negocio por defecto (la primera; se pueden combinar)
      const units = this.$store.state.auth.bussines_units;
      if (units && units.length) {
        const firstId = units[0].id ?? units[0].value;
        this.selectedBusiness = [firstId];
        this.prevBusiness = [firstId];
      }

      // Departamentos, proyectos y estatus
      this.itemsDepartment = [{ value: 0, text: "Todos" }, ...(await this.g_departments())];

      const projRes = await this.api_Get("project/projects");
      const projects = await projRes.json();
      this.itemsProjects = [{ id: 0, name: "Todos" }, ...(projects || [])];

      const statusRes = await this.api_Get("shopping/order_status");
      this.statusItems = (await statusRes.json()) || [];
      // Por defecto todos los estatus excepto Borrador (1), Cancelado (11) y Rechazado (12)
      this.selectedStatuses = this.statusItems
        .map((s) => s.id)
        .filter((id) => !this.excludedByDefault.includes(id));

      this.emitFilters();
    },
    onBusinessChange(val) {
      const had0 = this.prevBusiness.includes(0);
      const has0 = val.includes(0);
      if (!had0 && has0) {
        // El usuario acaba de escoger "Todas"
        val = [0];
      } else if (has0 && val.length > 1) {
        // Escogió una unidad específica teniendo "Todas" puesto
        val = val.filter((v) => v !== 0);
      }
      if (!val.length) val = [0];
      this.selectedBusiness = val;
      this.prevBusiness = [...val];
      // Al cambiar de unidad, se reinician las locations.
      this.selectedLocations = [0];
      this.prevLocations = [0];
      this.emitFilters();
    },
    onLocationsChange(val) {
      const had0 = this.prevLocations.includes(0);
      const has0 = val.includes(0);
      if (!had0 && has0) {
        // El usuario acaba de escoger "Todos"
        val = [0];
      } else if (has0 && val.length > 1) {
        // Escogió una location específica teniendo "Todos" puesto
        val = val.filter((v) => v !== 0);
      }
      if (!val.length) val = [0];
      this.selectedLocations = val;
      this.prevLocations = [...val];
      this.debouncedEmit();
    },
    toggleAllStatuses() {
      if (this.allStatusesSelected) {
        this.selectedStatuses = [];
      } else {
        this.selectedStatuses = this.statusItems.map((s) => s.id);
      }
      this.debouncedEmit();
    },
    updateRange() {
      const r = this.rangeSelected;
      if (r === "1") {
        this.dateStart = moment().format("YYYY-MM-DD");
        this.dateEnd = moment().format("YYYY-MM-DD");
      } else if (r === "2") {
        this.dateStart = moment().startOf("week").format("YYYY-MM-DD");
        this.dateEnd = moment().endOf("week").format("YYYY-MM-DD");
      } else if (r === "3") {
        this.dateStart = moment().startOf("month").format("YYYY-MM-DD");
        this.dateEnd = moment().endOf("month").format("YYYY-MM-DD");
      } else if (r === "4") {
        this.dateStart = moment().startOf("year").format("YYYY-MM-DD");
        this.dateEnd = moment().endOf("year").format("YYYY-MM-DD");
      }
      if (r !== "5") this.debouncedEmit();
    },
    dateFormatDMY(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
    debouncedEmit() {
      clearTimeout(this._emitTimer);
      this._emitTimer = setTimeout(() => this.emitFilters(), 350);
    },
    emitFilters() {
      if (!this.selectedBusiness || !this.selectedBusiness.length) return;
      const business_ids = this.selectedBusiness.includes(0)
        ? []
        : [...this.selectedBusiness];
      const location_ids = this.selectedLocations.includes(0)
        ? []
        : [...this.selectedLocations];
      this.$emit("applyFilters", {
        business_ids,
        location_ids,
        department_id: this.department_id || 0,
        project_id: this.project_id || 0,
        statuses: [...this.selectedStatuses],
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
      });
    },
  },
};
</script>
